import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';

import Button from '../../Components/Ui/Button';
import toastService from '../../services/toastService';
import Card from '../../Components/Ui/Card';
import {
  useGetAllBrandsQuery,
  useGetUserInfoQuery,
  useApplyForBrandsMutation,
} from '../../app/services/authApi';
import useFileUpload from '../../hooks/fileUpload';

const ApplyForBrands = () => {
  const { user } = useSelector((state) => state.userInfo);
  const { data: brandsData, isLoading: brandsLoading } = useGetAllBrandsQuery();
  const { data: userInfo, isLoading: userLoading } = useGetUserInfoQuery(user._id);
  const [applyForBrands, { isLoading: isApplying }] = useApplyForBrandsMutation();

  const [selectedBrands, setSelectedBrands] = useState([]);
  const [sellerAuthCertificates, setSellerAuthCertificates] = useState({});
  const { uploadFile, uploading } = useFileUpload();

  const existingBrandIds = useMemo(() => {
    return new Set(userInfo?.data?.brandDeal?.map((deal) => deal.brand) || []);
  }, [userInfo]);

  const availableBrands = useMemo(() => {
    return brandsData?.data.filter((brand) => !existingBrandIds.has(brand._id)) || [];
  }, [brandsData, existingBrandIds]);

  const handleBrandChange = (e) => {
    const brandId = e.target.value;
    const isChecked = e.target.checked;
    if (isChecked) {
      setSelectedBrands([...selectedBrands, brandId]);
    } else {
      setSelectedBrands(selectedBrands.filter((id) => id !== brandId));
      const newAuthCerts = { ...sellerAuthCertificates };
      delete newAuthCerts[brandId];
      setSellerAuthCertificates(newAuthCerts);
    }
  };

  const handleFileChange = (e, brandId) => {
    const file = e.target.files[0];
    if (file) {
      setSellerAuthCertificates({ ...sellerAuthCertificates, [brandId]: file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedBrands.length === 0) {
      toastService.warning('Please select at least one brand to apply for.');
      return;
    }

    try {
      const brandDeals = await Promise.all(
        selectedBrands.map(async (brandId) => {
          let certificate = '';
          if (sellerAuthCertificates[brandId]) {
            certificate = await uploadFile(sellerAuthCertificates[brandId]);
          }
          return { brand: brandId, certificate };
        }),
      );

      await applyForBrands({ id: user._id, brandDeals }).unwrap();
      toastService.success('Successfully applied for new brands!');
      setSelectedBrands([]);
      setSellerAuthCertificates({});
    } catch (error) {
      toastService.error('Failed to apply for new brands.');
    }
  };

  if (brandsLoading || userLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Card title="Apply for More Brands">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold tertiary-text-color pb-2 border-b-2 border-purple-200 mb-4">
            Available Brands
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {availableBrands.map((brand) => (
              <div
                key={brand._id}
                className="flex items-center p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <input
                  type="checkbox"
                  id={`brand-${brand._id}`}
                  value={brand._id}
                  checked={selectedBrands.includes(brand._id)}
                  onChange={handleBrandChange}
                  className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
                <label
                  htmlFor={`brand-${brand._id}`}
                  className="ml-3 flex items-center text-lg font-semibold tertiary-text-color cursor-pointer"
                >
                  {brand.brandLogo && (
                    <img
                      src={brand.brandLogo}
                      alt={`${brand.name} Logo`}
                      className="w-12 h-12 rounded-full mr-3 object-contain border border-gray-200 p-1"
                    />
                  )}
                  {brand.name}
                </label>
              </div>
            ))}
          </div>
        </div>

        {selectedBrands.length > 0 && (
          <div>
            <h3 className="text-xl font-semibold tertiary-text-color pb-2 border-b-2 border-purple-200 mb-4">
              Upload Authenticity Certificates
            </h3>
            <div className="space-y-4">
              {selectedBrands.map((brandId) => {
                const brand = availableBrands.find((b) => b._id === brandId);
                return (
                  <div key={brandId}>
                    <label className="block text-base font-medium text-secondary mb-1">
                      {brand.name}
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, brandId)}
                      className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={uploading || isApplying}>
            {uploading ? 'Uploading...' : isApplying ? 'Applying...' : 'Apply for Selected Brands'}
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default ApplyForBrands;
